import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Stack } from '@mui/material';

import type { FilterValues } from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';

import FilterChipGroup from './FilterChipGroup';
import useContainerOverflow from './useContainerOverflow';

import { styles } from './index.styles';

interface FilterDisplayProps {
  formatLabel(name: string, values: FilterValues): string;

  onDeleteFilter?: () => void;
  readonly?: boolean;
  withShowMoreButton?: boolean;
}

const FILTER_DISPLAY_WIDTH = '9.25rem';

const FilterDisplay: FC<FilterDisplayProps> = ({
  onDeleteFilter = () => {},
  formatLabel,
  readonly = false,
  withShowMoreButton = false,
}) => {
  const { t } = useTranslation();

  const [showAll, setShowAll] = useState(!withShowMoreButton);
  const { visibleFilterGroups, reset: resetFilter } = useFilterContext();

  const filterEntries = Object.entries(visibleFilterGroups);

  let renderedFiltersCounter = 0;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resetButtonRef = useRef<HTMLButtonElement | null>(null);

  const overflowCount = useContainerOverflow({
    containerRef,
    resetButtonRef,
  });
  const maximumChips =
    Object.values(visibleFilterGroups).flat().length - overflowCount;

  return (
    <Stack direction="row" spacing={1}>
      <Stack
        direction="row"
        ref={containerRef}
        sx={{
          alignItems: 'center',
          flexWrap: showAll ? 'wrap' : 'nowrap',
          maxWidth: withShowMoreButton
            ? `calc(100% - ${FILTER_DISPLAY_WIDTH})`
            : '100%',
        }}
      >
        {filterEntries.map(([name, values], indexEntry) => {
          if (renderedFiltersCounter >= maximumChips && !showAll) return;
          renderedFiltersCounter += values.length;
          const renderAnd =
            filterEntries.length > 1 &&
            indexEntry !== filterEntries.length - 1 &&
            !(renderedFiltersCounter >= maximumChips && !showAll);
          return (
            <FilterChipGroup
              key={`${name}-${indexEntry}`}
              filterName={name}
              values={values}
              renderAnd={renderAnd}
              maximumChips={maximumChips}
              renderedFiltersCounter={renderedFiltersCounter - values.length}
              showAll={showAll}
              formatLabel={formatLabel}
              onDeleteFilter={onDeleteFilter}
            />
          );
        })}

        {!!filterEntries.length && !readonly && (
          <Stack sx={{ pb: showAll ? 1 : 0, pl: 1 }}>
            <Button
              ref={resetButtonRef}
              variant="cta"
              color="secondary"
              onClick={() => resetFilter()}
              startIcon={<DeleteRounded />}
            >
              {t('common:resetFilter')}
            </Button>
          </Stack>
        )}
      </Stack>
      {withShowMoreButton && !!overflowCount && (
        <Box sx={{ minWidth: FILTER_DISPLAY_WIDTH }}>
          {Object.values(visibleFilterGroups).flat().length > maximumChips && (
            <Button
              variant="text"
              onClick={() => setShowAll((prev) => !prev)}
              endIcon={showAll ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              sx={styles.showMoreButton}
            >
              {overflowCount && showAll
                ? t('common:showLess')
                : t('common:showMore', { amount: overflowCount })}
            </Button>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default FilterDisplay;
